import {ErrorHandler, inject, Injectable, isDevMode, NgZone} from '@angular/core';
import {ApErrorhandlerService}                             from './ap-errorhandler.service';
import {has}                                 from 'lodash';
import {NotifyStore}                         from '../../stores/dialog/notify.store';
import {LoginStore}                          from '../../stores/login/login.store';
import IResultMessage = Invoker.IResultMessage;

const errorBucket: Array<any> = [];
const warningMessages = [
  'Cannot read properties of null (reading \'usedTiles\')',
  'null is not an object (evaluating \'U.usedTiles\')',
  'AP_WARN_'];

@Injectable({providedIn: 'root'})
export class ApErrorHandler implements ErrorHandler {
  private readonly ngZone = inject(NgZone);

  constructor(private loginStore: LoginStore,
              private errorHandlerService: ApErrorhandlerService,
              private notifyStore: NotifyStore) {
    // Intercept subscription decoupling so that an error here never blocks the handler
    this.errorHandlerService.confirmedError$.subscribe({
      next: (data: IResultMessage) => {
        try {
          errorBucket.push({ farmId: data.Data?.FarmId, message: data.Data?.Message });
        } catch (error: any) {
          console.log(error?.message);
        }
      },
      error: (err) => console.error('Error in confirmedError$ subscription:', err)
    });
  }

  /***
   * Tries to determin the current farmId
   */
  tryGetFarmId(): number {
    try {
      return this.loginStore.SelectedFarm.Id;
    } catch {
      return 0;
    }
  }

  /**
   * Handles an exception/error which was either raised on client side
   * or passed from backend to client.
   */
  handleError(error: any): void {
    // BREAK THE CALL STACK:
    // Using `setTimeout(..., 0)`, we defer the error handling to the next macrotask.
    // The original RxJS stream can cleanly complete its unsubscribe process
    // without causing recursion to build up in the call stack!
    setTimeout(() => {
      this.processError(error);
    }, 0);
  }

  private processError(error: any): void {
    // use innerException if any.
    // this is needed in case of exception within promises.
    if (has(error, 'rejection')) {
      error = error.rejection;
    }

    const IS_VALIDATION_ERROR = has(error, 'ValidationResults');
    const IS_WARNING = this.isWarning(error);

    try {
      let level = 'error';
      const errorState = this._safeStringify(error);
      const farmId = this.tryGetFarmId();
      const meta = {
        Url: window?.location?.href,
        Browser: this._getBrowser(),
        UserAgent: window.navigator?.userAgent
      };

      if (IS_VALIDATION_ERROR) {
        // Run UI Updates Safely in the Angular Zone
        this.ngZone.run(() => {
          error.ValidationResults.forEach((note: any) => {
            this.notifyStore.addMessage(note);
          });
        });
      } else if (IS_WARNING) {
        // Here we mark the error as a warning because it is well known
        // and does not break anything. Furthermore, we are not showing it to the user
        level = 'warning';
        console.warn(error);
      } else {
        this.ngZone.run(() => {
          this.loginStore.setClientErrorOccurred(true);
        });
        console.error(error);
      }

      const errorMessage = error?.message || String(error);

      // whenever the error has not yet been confirmed(=logged by server) the error will be sent to the gateway.
      if (!errorBucket.some(e => e.farmId === farmId && e.message === errorMessage)) {
        this.errorHandlerService.LogClientError(
          errorMessage,
          farmId,
          error?.stack,
          errorState,
          JSON.stringify(meta),
          level
        );
      }
    } catch (loggingError) {
      // in case the error cannot be sent to our backend we are logging this particular error to the browser console, only.
      // otherwise we might create an infinite loop
      console.error(error);
    }
  }

  private _safeStringify(obj: any): string {
    try {
      return JSON.stringify(obj);
    } catch {
      return '[Unserializable Object]'; // Prevents crashes with circular objects!
    }
  }

  private _getBrowser(): string {
    const agent = window.navigator.userAgent.toLowerCase();
    switch (true) {
      case agent.indexOf('edge') > -1 || agent.indexOf('edg') > -1:
        return 'edge';
      case agent.indexOf('opr') > -1:
        return 'opera';
      case agent.indexOf('chrome') > -1:
        return 'chrome';
      case agent.indexOf('trident') > -1:
        return 'ie';
      case agent.indexOf('firefox') > -1:
        return 'firefox';
      case agent.indexOf('safari') > -1:
        return 'safari';
      default:
        return 'other';
    }
  }

  /**
   * Checks if the caught client error refers to an error
   * which we want to handle as a warning because it usually does not
   * have any impact on our application
   * @param error
   * @private
   */
  private isWarning(error: Error): boolean {
    return warningMessages.some(errorText =>
      error?.stack?.includes(errorText)) ||
      warningMessages.some(errorText =>
      error?.message?.includes(errorText))  ||
      warningMessages.some(errorText =>
        error?.toString()?.includes(errorText));
  }
}
